import { FOGBUGZ_FORMULATOR_EVTX_MAX_BYTES, FOGBUGZ_FORMULATOR_SCREENSHOT_MAX_BYTES, } from "../fogbugz-formulator/constants.js";
import { FOGBUGZ_PROJECT_NOT_LINKED } from "../fogbugz-formulator/errors.js";
// ---------------------------------------------------------------------------
// Tag, marker line and timeouts
// ---------------------------------------------------------------------------
/**
 * The ADO tag (and Repro Steps marker) of a Bug brought from FogBugz case
 * `caseId`: `fogbugz-<caseId>`. The duplicate check looks for it (and the
 * Hyperlink to the case) before a second Bug is created.
 */
export function FOGBUGZ_TO_ADO_TAG(caseId) {
    return `fogbugz-${String(caseId)}`;
}
/**
 * The marker the internal skill prints at the start of its one result line:
 * `SPECTREE_FOGBUGZ_TO_ADO {json}`. The JSON parses with
 * `fogbugzToAdoSlotsMarkerSchema` after a slots run and with
 * `fogbugzToAdoDraftMarkerSchema` after a draft run.
 */
export const FOGBUGZ_TO_ADO_TURN_MARKER = "SPECTREE_FOGBUGZ_TO_ADO";
/** How long `readSlots` may run (Claude reads the case with the code open) before the engine stops it with `TIMED_OUT`. */
export const FOGBUGZ_TO_ADO_SLOTS_TIMEOUT_MS = 10 * 60_000;
/** How long `draftBug` may run before the engine stops it with `TIMED_OUT`. */
export const FOGBUGZ_TO_ADO_DRAFT_TIMEOUT_MS = 15 * 60_000;
// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------
/**
 * Largest case image sent to Claude for a written description, in bytes
 * (10 MiB, the FogBugz Formulator's screenshot cap). A larger image is not
 * described, and not attached either (the same 10 MiB is the per-file
 * attachment cap): it stays on the FogBugz case. No count cap.
 */
export const FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES = FOGBUGZ_FORMULATOR_SCREENSHOT_MAX_BYTES;
/**
 * Largest single case file uploaded to the ADO Bug as an attachment, in bytes
 * (10 MiB, the same as `EVTX_MAX_BYTES`). A larger file stays on the FogBugz
 * case and the result lists it as `too-large`.
 */
export const FOGBUGZ_TO_ADO_ATTACHMENT_MAX_BYTES = FOGBUGZ_FORMULATOR_EVTX_MAX_BYTES;
/**
 * Most bytes uploaded to one Bug, all attachments together (60 MiB). Files
 * past it stay on the FogBugz case and the result lists them as `too-large`.
 */
export const FOGBUGZ_TO_ADO_TOTAL_ATTACHMENT_MAX_BYTES = 60 * 1024 * 1024;
/** Longest case attachment file name. */
export const FOGBUGZ_TO_ADO_FILE_NAME_MAX = 200;
/** Most case attachments one bundle lists (images, event logs and other files together). */
export const FOGBUGZ_TO_ADO_ATTACHMENTS_MAX = 500;
/** The kinds of case attachment the tool tells apart. */
export const FOGBUGZ_TO_ADO_ATTACHMENT_KINDS = ["image", "evtx", "other"];
/** What happened to one case file on create. */
export const FOGBUGZ_TO_ADO_ATTACHMENT_STATUSES = ["attached", "too-large", "failed"];
/** Longest attachment-proxy path a bundle carries for one case file. */
export const FOGBUGZ_TO_ADO_PROXY_PATH_MAX = 4_096;
/** Whether create added the Bug's Hyperlink back to the FogBugz case. */
export const FOGBUGZ_TO_ADO_HYPERLINK_STATUSES = ["added", "failed"];
// ---------------------------------------------------------------------------
// Character caps
// ---------------------------------------------------------------------------
/** Most case events a bundle carries, oldest first. */
export const FOGBUGZ_TO_ADO_HISTORY_EVENTS_MAX = 200;
/** Longest Bug title (the ADO title limit). */
export const FOGBUGZ_TO_ADO_TITLE_MAX = 255;
/** Longest drafted text section (expected, actual, environment). */
export const FOGBUGZ_TO_ADO_TEXT_SECTION_MAX = 20_000;
/** Longest digest of the case history the draft carries. */
export const FOGBUGZ_TO_ADO_HISTORY_DIGEST_MAX = 8_000;
/** Longest written description of one screenshot. */
export const FOGBUGZ_TO_ADO_SCREENSHOT_DESCRIPTION_MAX = 2_000;
/** Longest event-log summary text the draft carries for one `.evtx` file. */
export const FOGBUGZ_TO_ADO_EVTX_SUMMARY_MAX = 12_000;
/** Fewest numbered repro steps a draft carries (the ADO grooming bar for a Bug). */
export const FOGBUGZ_TO_ADO_REPRO_STEPS_MIN = 2;
/** Most numbered repro steps a draft carries. */
export const FOGBUGZ_TO_ADO_REPRO_STEPS_MAX = 50;
/** Longest single repro step. */
export const FOGBUGZ_TO_ADO_REPRO_STEP_MAX = 2_000;
/** Shortest environment text a draft carries (the ADO grooming bar for a Bug: at least 10 characters). */
export const FOGBUGZ_TO_ADO_ENVIRONMENT_MIN = 10;
/** Longest value Claude or the PM gives one slot. */
export const FOGBUGZ_TO_ADO_SLOT_VALUE_MAX = 4_000;
/** Longest evidence pointer on a found slot (`event 3`, `intent.md`, `screenshot login.png`, ...). */
export const FOGBUGZ_TO_ADO_EVIDENCE_MAX = 1_000;
/** Most questions for the reporter one below-bar case gets. */
export const FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX = 15;
/** Longest question for the reporter. */
export const FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX = 500;
/** Longest PM name the composers and bridge inputs take. */
export const FOGBUGZ_TO_ADO_PM_NAME_MAX = 200;
/** Longest ADO iteration path. */
export const FOGBUGZ_TO_ADO_ITERATION_PATH_MAX = 512;
/** Most iterations the classification response lists (the project root included). */
export const FOGBUGZ_TO_ADO_ITERATIONS_MAX = 500;
// ---------------------------------------------------------------------------
// Severity
// ---------------------------------------------------------------------------
/** The `Microsoft.VSTS.Common.Severity` values of an ADO Bug, most severe first. */
export const FOGBUGZ_TO_ADO_SEVERITIES = ["1 - Critical", "2 - High", "3 - Medium", "4 - Low"];
// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------
/**
 * The Intent v1 slots a bug owes (`KIND_SLOT_SETS.bug.required` in
 * `packages/shared/src/intent/slots.ts`), in playbook order: the slots the
 * tool reads from a case, one verdict each. A subset of the fogbugz-formulator
 * contract's slot ids (`FOGBUGZ_FORMULATOR_SLOT_IDS`), so the slot enum is
 * that contract's; a `packages/shared` test proves the list equals Intent v1.
 */
export const FOGBUGZ_TO_ADO_BUG_SLOT_IDS = [
    "title",
    "symptom",
    "reproSteps",
    "expectedVsActual",
    "usersAffected",
    "frequency",
    "environment",
    "severity",
    "reproducibility",
    "workaround",
    "boundary",
];
/** True when `slot` is one of the {@link FOGBUGZ_TO_ADO_BUG_SLOT_IDS}. */
export function isFogbugzToAdoBugSlotId(slot) {
    return typeof slot === "string" && FOGBUGZ_TO_ADO_BUG_SLOT_IDS.includes(slot);
}
/**
 * The plain label of each bug slot: Intent v1's slot labels, so the reporter
 * note and the created note name a slot as `intent.md` does.
 */
export const FOGBUGZ_TO_ADO_SLOT_LABELS = {
    title: "Title",
    symptom: "Symptom",
    reproSteps: "Steps to reproduce",
    expectedVsActual: "Expected vs actual",
    usersAffected: "Users affected",
    frequency: "Frequency",
    environment: "Environment",
    severity: "Severity",
    reproducibility: "Reproducibility",
    workaround: "Workaround",
    boundary: "Boundary (out of scope)",
};
/** Whether Claude found a slot's value in the case (with evidence) or not. A guess is `missing`. */
export const FOGBUGZ_TO_ADO_SLOT_STATUSES = ["found", "missing"];
/**
 * How the case met the bar on create: `complete` (every bug slot found in the
 * case) or `pm-filled` (the PM filled the missing slots in a short interview;
 * the FogBugz note records what the PM added). Resolved Decision 7.
 */
export const FOGBUGZ_TO_ADO_BAR_MODES = ["complete", "pm-filled"];
// ---------------------------------------------------------------------------
// Run phases
// ---------------------------------------------------------------------------
/** The phases a run reports through `onProgress`. Pages show {@link FOGBUGZ_TO_ADO_RUN_PHASE_LABELS}, never the id. */
export const FOGBUGZ_TO_ADO_RUN_PHASES = ["checking-code", "reading-case", "drafting-bug"];
/** The plain phrase the page shows for each phase. */
export const FOGBUGZ_TO_ADO_RUN_PHASE_LABELS = {
    "checking-code": "Checking the product code",
    "reading-case": "Reading the case",
    "drafting-bug": "Drafting the Bug",
};
// ---------------------------------------------------------------------------
// Prepare refusals (SpecTree API)
// ---------------------------------------------------------------------------
/** No saved FogBugz key: the case cannot be read as the PM. */
export const FOGBUGZ_TO_ADO_FOGBUGZ_KEY_MISSING = "FOGBUGZ_KEY_MISSING";
/** No saved ADO PAT: the Bug cannot be created as the PM. */
export const FOGBUGZ_TO_ADO_ADO_PAT_MISSING = "ADO_PAT_MISSING";
/**
 * The case's FogBugz category is not Bug. E311 brings Bugs only: a Feature
 * case gets a hand-off to the ADO Formulator (a User Story), an Inquiry case
 * a plain message (answer it in FogBugz).
 */
export const FOGBUGZ_TO_ADO_NOT_A_BUG_CASE = "NOT_A_BUG_CASE";
/** The linked ADO project's repository copies are missing (no mapped repos, or not set up). */
export const FOGBUGZ_TO_ADO_REPOS_NOT_SET_UP = "REPOS_NOT_SET_UP";
/** An ADO Bug already carries this case's tag or Hyperlink; nothing is created twice. */
export const FOGBUGZ_TO_ADO_DUPLICATE_FOUND = "DUPLICATE_FOUND";
/** Every code a prepare call refuses a case with (before any Claude run or ADO write). */
export const FOGBUGZ_TO_ADO_REFUSAL_CODES = [
    FOGBUGZ_TO_ADO_FOGBUGZ_KEY_MISSING,
    FOGBUGZ_TO_ADO_ADO_PAT_MISSING,
    FOGBUGZ_TO_ADO_NOT_A_BUG_CASE,
    FOGBUGZ_PROJECT_NOT_LINKED,
    FOGBUGZ_TO_ADO_REPOS_NOT_SET_UP,
    FOGBUGZ_TO_ADO_DUPLICATE_FOUND,
];
/** True when `value` is a prepare refusal code. */
export function isFogbugzToAdoRefusalCode(value) {
    return typeof value === "string" && FOGBUGZ_TO_ADO_REFUSAL_CODES.includes(value);
}
// ---------------------------------------------------------------------------
// Engine error codes (SpecTree Toolbox)
// ---------------------------------------------------------------------------
/** The fogbugz toolset's engine codes this engine shares, imported by type so they cannot drift. */
const SHARED_ENGINE_ERROR_CODES = [
    /** Claude Code is not installed or cannot be found. */
    "CLAUDE_NOT_FOUND",
    "CLAUDE_NOT_SIGNED_IN",
    /** Claude Code would bill an API key rather than the person's subscription seat. */
    "API_KEY_BILLING",
    /** The person's Claude usage limit is reached; the message names the reset time. */
    "USAGE_LIMIT",
    /** The run went past `FOGBUGZ_TO_ADO_SLOTS_TIMEOUT_MS` or `FOGBUGZ_TO_ADO_DRAFT_TIMEOUT_MS`. */
    "TIMED_OUT",
    /** The bundled plugin with the fogbugz-to-ado skill is missing. */
    "PLUGIN_MISSING",
    /** The PM canceled the run. */
    "CANCELED",
    /** The app is already running a fogbugz-to-ado step (one at a time per app). */
    "BUSY",
    /** Stopped before Claude started: Claude Code did not apply the read-only settings, or the private folder could not be made. */
    "REFUSED",
    /** Claude Code could not reach Anthropic. */
    "NETWORK",
    /** Anything else; the message is the engine's plain sentence. */
    "UNKNOWN",
];
/**
 * Every code the fogbugz-to-ado engine resolves a bridge call with: the
 * shared fogbugz engine codes and its own.
 */
export const FOGBUGZ_TO_ADO_ENGINE_ERROR_CODES = [
    ...SHARED_ENGINE_ERROR_CODES,
    /** The linked ADO project's repository copies are not on this computer; `missing` names them. */
    "REPOS_NOT_COPIED",
    /**
     * No repositories are mapped for the linked ADO project in SpecTree, so
     * there is nothing to copy or read: an Admin sets them up on the Toolbox
     * Repositories page (the engine-side twin of the prepare refusal
     * `REPOS_NOT_SET_UP`).
     */
    "REPOS_NOT_SET_UP",
    /** The repository copies are being updated by another run; try again shortly. */
    "REPO_LOCK_BUSY",
    /** Claude finished a slots run without a valid `SPECTREE_FOGBUGZ_TO_ADO` slots line. */
    "NO_SLOTS_RETURNED",
    /** Claude finished a draft run without a valid `SPECTREE_FOGBUGZ_TO_ADO` draft line. */
    "NO_DRAFT_RETURNED",
];
/** True when `value` is a fogbugz-to-ado engine error code. */
export function isFogbugzToAdoEngineErrorCode(value) {
    return typeof value === "string" && FOGBUGZ_TO_ADO_ENGINE_ERROR_CODES.includes(value);
}
//# sourceMappingURL=constants.js.map